import { ChevronLeftIcon, ChevronRightIcon, PauseIcon, PlayIcon } from "@heroicons/react/24/solid";
import { Button } from "@moe/oss/ui/button";
import { Tooltip, TooltipContent, TooltipTrigger } from "@moe/oss/ui/tooltip";
import { useAppContext } from "@web/route-services/root/AppContext";
import { EmblaCarouselType } from "embla-carousel";
import Autoplay from "embla-carousel-autoplay";
import useEmblaCarousel from "embla-carousel-react";
import { motion } from "motion/react";
import React, { ReactNode, useCallback, useEffect, useState } from "react";

interface CarouselProps<T> {
  items: T[];
  renderItem: (item: T) => ReactNode;
  itemKeyFn: (item: T, index: number) => string | number;
  autoplayDelay?: number;
  showControls?: boolean;
}

export function Carousel<T>(props: CarouselProps<T>) {
  const { isDesktopScreen } = useAppContext();
  if (isDesktopScreen) {
    return <EmblaCarousel {...props} />;
  }
  return <MobileScroll {...props} />;
}

function EmblaCarousel<T>({
  items,
  renderItem,
  itemKeyFn,
  autoplayDelay = 3000,
  showControls = true
}: CarouselProps<T>) {
  const [emblaRef, emblaApi] = useEmblaCarousel(undefined, [Autoplay({ playOnInit: true, delay: autoplayDelay })]);
  const [isPlaying, setIsPlaying] = useState(false);
  const { prevBtnDisabled, nextBtnDisabled, onPrevButtonClick, onNextButtonClick } = usePrevNextButtons(emblaApi);

  // Add effect to handle dynamic height adjustments
  useEffect(() => {
    if (!emblaApi) return;

    const adjustHeight = () => {
      // Get all slides
      const slides = emblaApi.slideNodes();
      // Reset heights to auto to get true heights
      slides.forEach((slide: HTMLElement) => {
        slide.style.height = "auto";
      });

      // Find the tallest slide
      const maxHeight = Math.max(...slides.map((slide: HTMLElement) => slide.offsetHeight));
      // Set all slides to the tallest height
      slides.forEach((slide: HTMLElement) => {
        slide.style.height = `${maxHeight}px`;
      });
    };

    // Run on init and whenever slides change
    adjustHeight();
    window.addEventListener("resize", adjustHeight);
    emblaApi.on("reInit", adjustHeight);
    emblaApi.on("slidesInView", adjustHeight);

    return () => {
      window.removeEventListener("resize", adjustHeight);
    };
  }, [emblaApi]);

  const onButtonAutoplayClick = useCallback(
    (callback: () => void) => {
      const autoplay = emblaApi?.plugins()?.autoplay;
      if (!autoplay) return;
      const resetOrStop = autoplay.options.stopOnInteraction === false ? autoplay.reset : autoplay.stop;
      resetOrStop();
      callback();
    },
    [emblaApi]
  );

  const toggleAutoplay = useCallback(() => {
    const autoplay = emblaApi?.plugins()?.autoplay;
    if (!autoplay) return;

    const playOrStop = autoplay.isPlaying() ? autoplay.stop : autoplay.play;
    playOrStop();
  }, [emblaApi]);

  useEffect(() => {
    const autoplay = emblaApi?.plugins()?.autoplay;
    if (!autoplay) return;

    setIsPlaying(autoplay.isPlaying());
    emblaApi
      .on("autoplay:play", () => setIsPlaying(true))
      .on("autoplay:stop", () => setIsPlaying(false))
      .on("reInit", () => setIsPlaying(autoplay.isPlaying()));
  }, [emblaApi]);

  return (
    <div className="embla relative w-full">
      <div className="embla__viewport overflow-hidden" ref={emblaRef}>
        <div className="embla__container py-1 pl-2">
          {items.map((item, index) => (
            <CarouselItem
              key={itemKeyFn(item, index)}
              item={item}
              index={index}
              renderItem={renderItem}
              itemKeyFn={itemKeyFn}
            />
          ))}
        </div>
      </div>

      {showControls && (
        <div className="embla__controls">
          <div className="embla__buttons">
            <PrevButton onClick={() => onButtonAutoplayClick(onPrevButtonClick)} disabled={prevBtnDisabled} />
            <NextButton onClick={() => onButtonAutoplayClick(onNextButtonClick)} disabled={nextBtnDisabled} />
          </div>

          <Tooltip>
            <TooltipTrigger className="embla__play">
              <div
                className="border-line flex size-9 cursor-pointer items-center justify-center rounded-full border p-2 shadow-md hover:brightness-110"
                onClick={toggleAutoplay}
              >
                {isPlaying ? (
                  <PauseIcon className="text-tx-secondary size-6" />
                ) : (
                  <PlayIcon className="text-tx-secondary size-6" />
                )}
              </div>
            </TooltipTrigger>
            <TooltipContent>
              <p>Toggle Carousel Autoplay</p>
            </TooltipContent>
          </Tooltip>
        </div>
      )}
    </div>
  );
}

const CarouselItem = React.memo<{
  item: any;
  index: number;
  renderItem: (item: any) => ReactNode;
  itemKeyFn: (item: any, index: number) => string | number;
}>(({ item, index, renderItem, itemKeyFn }) => (
  <motion.div
    className="embla__slide pl-4"
    key={itemKeyFn(item, index)}
    initial={{ opacity: 0, scale: 0.95 }}
    animate={{ opacity: 1, scale: 1 }}
    transition={{
      duration: 0.2,
      ease: "easeOut"
    }}
  >
    {renderItem(item)}
  </motion.div>
));

const PrevButton: React.FC<NextPrevProps> = (props) => {
  const { children, ...rest } = props;

  return (
    <Button variant="shell" {...rest}>
      <ChevronLeftIcon className="text-tx-secondary size-4" />
      {children}
    </Button>
  );
};

const NextButton: React.FC<NextPrevProps> = (props) => {
  const { children, ...rest } = props;

  return (
    <Button variant="shell" {...rest}>
      <ChevronRightIcon className="text-tx-secondary size-4" />
      {children}
    </Button>
  );
};

type UsePrevNextButtonsType = {
  prevBtnDisabled: boolean;
  nextBtnDisabled: boolean;
  onPrevButtonClick: () => void;
  onNextButtonClick: () => void;
};

const usePrevNextButtons = (
  emblaApi: EmblaCarouselType | undefined,
  onButtonClick?: (emblaApi: EmblaCarouselType) => void
): UsePrevNextButtonsType => {
  const [prevBtnDisabled, setPrevBtnDisabled] = useState(true);
  const [nextBtnDisabled, setNextBtnDisabled] = useState(true);

  const onPrevButtonClick = useCallback(() => {
    if (!emblaApi) return;
    emblaApi.scrollPrev();
    if (onButtonClick) onButtonClick(emblaApi);
  }, [emblaApi, onButtonClick]);

  const onNextButtonClick = useCallback(() => {
    if (!emblaApi) return;
    emblaApi.scrollNext();
    if (onButtonClick) onButtonClick(emblaApi);
  }, [emblaApi, onButtonClick]);

  const onSelect = useCallback((emblaApi: EmblaCarouselType) => {
    setPrevBtnDisabled(!emblaApi.canScrollPrev());
    setNextBtnDisabled(!emblaApi.canScrollNext());
  }, []);

  useEffect(() => {
    if (!emblaApi) return;

    onSelect(emblaApi);
    emblaApi.on("reInit", onSelect).on("select", onSelect);
  }, [emblaApi, onSelect]);

  return {
    prevBtnDisabled,
    nextBtnDisabled,
    onPrevButtonClick,
    onNextButtonClick
  };
};

type NextPrevProps = React.ComponentPropsWithRef<"button">;

function MobileScroll<T>({ items, renderItem, itemKeyFn }: CarouselProps<T>) {
  return (
    <div className="scroll-primary overflow-x-auto py-1 pb-3 pl-2">
      <div className="flex space-x-4">
        {items.map((item, index) => (
          <div key={itemKeyFn(item, index)} className="flex-shrink-0">
            {renderItem(item)}
          </div>
        ))}
      </div>
    </div>
  );
}
